/**
 * Play-by-play, grouped by period and, for football, by drive
 * (ISSUE-986), sourced from `/event/plays-get`. Server resolves the
 * grouping and each drive's end score; this component only renders it.
 *
 * Periods, drives and plays arrive in server (chronological) order and are
 * reversed only while the game is live (`orderPlaysForDisplay`), matching
 * ISSUE-167's newest-first-while-live rule. While `isLive`, the query
 * refetches at the server's own `refreshAfterSeconds` so a new play appears
 * without a reload (R4).
 *
 * Fetches its own data so it only queries once this tab is actually
 * mounted; an inactive `Tabs` panel does not render its children
 * (`packages/ui/src/Tabs/Tabs.tsx`, no `forceMount`).
 */
import { useEventPlaysGet } from "@sportsmet/events-sdk/private";
import { cn, Skeleton } from "@sportsmet/ui";
import { replaceEqualDeep } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { PlaysPeriodSelector } from "./PlaysPeriodSelector";

import { TeamDisc } from "../scores/TeamDisc";
import {
  orderPlaysForDisplay,
  playsRefetchIntervalMs,
} from "./gameDetailLogic";
import { GameSectionCard, GameSectionEmpty } from "./GameSectionCard";

type typePlayWire = {
  entryId: string;
  clockLabel?: string | null;
  description: string;
  teamId?: string | null;
  homeScore?: number | null;
  awayScore?: number | null;
  isScoringPlay: boolean;
};

type typeDriveWire = {
  driveKey: string;
  teamId?: string | null;
  label?: string | null;
  startClockLabel?: string | null;
  playCount?: number | null;
  yards?: number | null;
  timeOfPossession?: string | null;
  endHomeScore?: number | null;
  endAwayScore?: number | null;
  isScoringDrive: boolean;
  plays: typePlayWire[];
};

type typePeriodWire = {
  periodNumber?: number | null;
  label: string;
  plays: typePlayWire[];
  drives: typeDriveWire[];
};

type typeTeamRef = {
  teamId: string;
  abbreviation: string;
  /** The team's mark for the 20px disc on each play; null draws the plate. */
  logoUrl?: string | null;
};

const resolveTeam = ({
  teamId,
  home,
  away,
}: {
  teamId?: string | null | undefined;
  home: typeTeamRef;
  away: typeTeamRef;
}): typeTeamRef | null => {
  if (teamId === home.teamId) return home;
  if (teamId === away.teamId) return away;
  return null;
};

const resolveAbbreviation = ({
  teamId,
  home,
  away,
}: {
  teamId?: string | null | undefined;
  home: typeTeamRef;
  away: typeTeamRef;
}): string | null => {
  if (teamId === home.teamId) return home.abbreviation;
  if (teamId === away.teamId) return away.abbreviation;
  return null;
};

const DriveSummary = ({
  drive,
  home,
  away,
}: {
  drive: typeDriveWire;
  home: typeTeamRef;
  away: typeTeamRef;
}): ReactNode => {
  const abbr = resolveAbbreviation({ teamId: drive.teamId, home, away });
  const detailParts = [
    drive.startClockLabel ? `at ${drive.startClockLabel}` : null,
    drive.playCount === 1
      ? "1 play"
      : drive.playCount !== null && drive.playCount !== undefined
        ? `${drive.playCount} plays`
        : null,
    drive.yards !== null && drive.yards !== undefined
      ? `${drive.yards} yds`
      : null,
    drive.timeOfPossession ?? null,
  ].filter((part): part is string => part !== null);

  const hasEndScore =
    typeof drive.endHomeScore === "number" &&
    typeof drive.endAwayScore === "number";

  return (
    <summary
      className={cn(
        "flex min-h-touch-target cursor-pointer items-center justify-between gap-2 text-body text-ink",
        // A scoring drive carries a 4px primary edge (spec 7.7).
        drive.isScoringDrive ? "border-l-4 border-primary pl-3" : undefined,
      )}
      data-scoring={drive.isScoringDrive ? "true" : undefined}
      data-testid="plays-drive-summary"
    >
      <span className="min-w-0">
        <span className={drive.isScoringDrive ? "text-body-strong" : undefined}>
          {abbr ? `${abbr} · ` : ""}
          {drive.label ?? "Drive"}
          {drive.isScoringDrive ? (
            <span className="sr-only">Scoring drive.</span>
          ) : null}
        </span>
        {detailParts.length > 0 ? (
          <span className="block text-small text-ink-secondary tabular-nums">
            {detailParts.join(", ")}
          </span>
        ) : null}
      </span>
      {hasEndScore ? (
        <span
          className={cn(
            "shrink-0 tabular-nums",
            drive.isScoringDrive
              ? "text-body-strong text-ink"
              : "text-small text-ink-secondary",
          )}
          data-testid="plays-drive-score"
        >
          {drive.endAwayScore}-{drive.endHomeScore}
        </span>
      ) : null}
    </summary>
  );
};

const PlayRow = ({
  showScoringStripe = false,
  play,
  home,
  away,
}: {
  play: typePlayWire;
  showScoringStripe?: boolean;
  home: typeTeamRef;
  away: typeTeamRef;
}): ReactNode => {
  const team = resolveTeam({ teamId: play.teamId, home, away });
  const hasScore =
    typeof play.homeScore === "number" && typeof play.awayScore === "number";
  // Spec 7.7: the clock in a fixed 52px column, a 20px team mark, 12, then
  // the description.
  return (
    <div
      className={cn(
        "flex min-h-touch-target items-center gap-3 border-t border-hairline py-2 text-body text-ink first:border-t-0",
        showScoringStripe && play.isScoringPlay
          ? "border-l-4 border-l-primary pl-3"
          : undefined,
      )}
      data-testid="plays-play"
      data-scoring={play.isScoringPlay ? "true" : undefined}
    >
      <span
        className="w-13 shrink-0 text-caption text-ink-secondary tabular-nums"
        data-testid="plays-play-clock"
      >
        {play.clockLabel ?? ""}
      </span>
      {team !== null ? (
        <TeamDisc
          plateText={team.abbreviation}
          logoUrl={team.logoUrl ?? null}
          size="mini"
          testId="plays-play-mark"
        />
      ) : (
        <span className="h-icon-md w-icon-md shrink-0" aria-hidden="true" />
      )}
      <span
        className={
          play.isScoringPlay
            ? "min-w-0 flex-1 text-body-strong"
            : "min-w-0 flex-1"
        }
        data-testid="plays-play-description"
      >
        {team !== null ? (
          <span className="sr-only">{team.abbreviation}: </span>
        ) : null}
        {play.description}
        {play.isScoringPlay ? (
          <span className="sr-only">Scoring play.</span>
        ) : null}
      </span>
      {play.isScoringPlay && hasScore ? (
        <span className="shrink-0 text-body-strong tabular-nums">
          {play.awayScore}-{play.homeScore}
        </span>
      ) : null}
    </div>
  );
};

const hasErrorEnvelope = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  "error" in value &&
  Boolean(value.error);

const keepLastGoodEnvelope = (oldData: unknown, newData: unknown): unknown => {
  if (hasErrorEnvelope(newData) && oldData !== undefined) {
    if (!hasErrorEnvelope(oldData)) return oldData;
  }
  return replaceEqualDeep(oldData, newData);
};

export const PlaysTab = ({
  eventId,
  isLive,
  home,
  away,
}: {
  eventId: string;
  isLive: boolean;
  home: typeTeamRef;
  away: typeTeamRef;
}): ReactNode => {
  const [selection, setSelection] = useState<{
    eventId: string;
    key: string;
  } | null>(null);
  const query = useEventPlaysGet(
    { eventId },
    {
      // A live poll that fails keeps the plays already on screen instead of
      // swapping them for the load error; only a first load that fails
      // shows it.
      refetchIntervalInBackground: false,
      structuralSharing: (oldData, newData) =>
        keepLastGoodEnvelope(oldData, newData),
      refetchInterval: (activeQuery) =>
        playsRefetchIntervalMs({
          isLive,
          refreshAfterSeconds:
            activeQuery.state.data?.data?.refreshAfterSeconds,
        }),
    },
  );

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2" data-testid="plays-tab-loading">
        <Skeleton shape="text" />
        <Skeleton shape="text" />
        <Skeleton shape="text" />
      </div>
    );
  }

  // The generated SDK resolves a failed call with `{ error, data: null }`
  // rather than rejecting (see src/queryClient.ts), so an error envelope is
  // a load failure too, never an empty game. A poll that throws puts the
  // query in "error" but keeps the last good data, so the load error shows
  // only when there is no good data to keep on screen.
  if (query.data === undefined || query.data.error) {
    return (
      <div className="flex flex-col gap-4" data-testid="plays-tab">
        <GameSectionCard>
          <GameSectionEmpty
            testId="plays-tab-error"
            line="Plays did not load. Try again in a moment."
          />
        </GameSectionCard>
      </div>
    );
  }

  const periods: typePeriodWire[] = query.data.data?.periods ?? [];

  if (periods.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="plays-tab">
        <GameSectionCard>
          <GameSectionEmpty testId="plays-tab-empty" line="No plays yet." />
        </GameSectionCard>
      </div>
    );
  }

  const sportKey = query.data.data?.sportKey ?? null;
  const useIntervals =
    sportKey === "football" ||
    sportKey === "basketball" ||
    sportKey === "hockey";
  const keyFor = (period: typePeriodWire) =>
    `${String(period.periodNumber ?? "unknown")}:${period.label}`;
  const fallback = isLive ? periods.at(-1) : periods[0];
  const activeKey =
    selection?.eventId === eventId &&
    periods.some((period) => keyFor(period) === selection.key)
      ? selection.key
      : fallback === undefined
        ? ""
        : keyFor(fallback);
  const visiblePeriods = useIntervals
    ? periods.filter((period) => keyFor(period) === activeKey)
    : periods;
  const orderedPeriods = orderPlaysForDisplay(visiblePeriods, isLive);

  return (
    <div className="flex flex-col gap-4" data-testid="plays-tab">
      {useIntervals && periods.length > 1 ? (
        <PlaysPeriodSelector
          options={periods.map((period) => ({
            value: keyFor(period),
            label: period.label,
          }))}
          value={activeKey}
          onChange={(key) => {
            setSelection({ eventId, key });
          }}
        />
      ) : null}
      {orderedPeriods.map((period) => {
        const orderedDrives = orderPlaysForDisplay(period.drives, isLive);
        const orderedPlays = orderPlaysForDisplay(period.plays, isLive);
        return (
          <GameSectionCard
            key={keyFor(period)}
            testId="plays-period"
            title={period.label}
          >
            {orderedDrives.map((drive) => (
              <details key={drive.driveKey} data-testid="plays-drive">
                <DriveSummary drive={drive} home={home} away={away} />
                <div className="flex flex-col pl-4">
                  {orderPlaysForDisplay(drive.plays, isLive).map((play) => (
                    <PlayRow
                      key={play.entryId}
                      play={play}
                      home={home}
                      away={away}
                    />
                  ))}
                </div>
              </details>
            ))}
            {orderedPlays.map((play) => (
              <PlayRow
                key={play.entryId}
                play={play}
                home={home}
                away={away}
                showScoringStripe={
                  sportKey === "baseball" || sportKey === "hockey"
                }
              />
            ))}
          </GameSectionCard>
        );
      })}
    </div>
  );
};
